import { c as _c } from "react/compiler-runtime";
import { useAnalytics } from '@repo/analytics/analytics-provider';
import { IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { ScrollableContent } from '@repo/design-system/components/layout/scrollable-content';
import { DropdownMenu, DropdownMenuItem } from '@repo/design-system/components/ui/dropdown-menu';
import { Modal } from '@repo/design-system/components/ui/modal';
import { toast } from '@repo/design-system/components/ui/toaster';
import { Type } from '@repo/design-system/components/ui/type';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectExploreActionMenuPinId, selectIsCompactViewport } from '@repo/store/maps/slices/clientContext';
import { selectCurrentMapInfo, selectIsEditingCustomPin } from '@repo/store/maps/slices/mapData';
import type { MapDataGameCoordinates } from '@repo/types/maps/map-types';
import { getFontInfoForGlyph, getIconForGlyph, getSubcategoryIconStyle } from '@repo/types/maps/map-utils';
import React, { useState } from 'react';
import { useCustomPinOperations } from '../game-map/hooks/useCustomPinOperations';
import { MapEmbedModal } from '../map-embed-modal/map-embed-modal';
import { useLocationInfo } from './hooks/useLocationInfo';
import { LocationActions } from './location-actions';
import { LocationContent } from './location-content';
import { LocationInfoMenu } from './location-info-menu';
import { getMapDescriptionLinkType } from './map-description-url';
import { PinMedia } from './pin-media/pin-media';
const CONTENT_PADDING = '20';
const CONTENT_PADDING_MOBILE = '16';
const CONTENT_PADDING_COMPACT = '12';
interface CategoryIconProps {
  categoryColor: string;
  subcategoryIcon: string;
  isMobilePane?: boolean;
  hasMedia?: boolean;
  onClick: () => void;
}
const categoryIconStyles = css.raw({
  width: '[32px]',
  height: '[32px]',
  minWidth: '[32px]',
  minHeight: '[32px]',
  borderRadius: 'full',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 'lg',
  color: 'white',
  boxShadow: 'sm',
  backdropFilter: '[blur(8px)]'
});
const mobileCategoryIconStyles = css.raw({
  width: '[32px]',
  height: '[32px]',
  minWidth: '[32px]',
  minHeight: '[32px]',
  borderRadius: 'full',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 'md',
  color: 'white',
  boxShadow: 'sm',
  backdropFilter: '[blur(8px)]'
});
const mobileMediaCategoryIconStyles = css.raw({
  width: '[48px]',
  height: '[48px]',
  minWidth: '[48px]',
  minHeight: '[48px]',
  borderRadius: 'full',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '2xl',
  color: 'white',
  boxShadow: 'sm',
  backdropFilter: '[blur(8px)]'
});
const CategoryIconButton: React.FC<CategoryIconProps> = t0 => {
  const $ = _c(20);
  const {
    categoryColor,
    subcategoryIcon,
    isMobilePane,
    hasMedia,
    onClick
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["game_map"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useT(t1);
  let iconChar;
  let t2;
  if ($[1] !== subcategoryIcon) {
    const fontInfo = getFontInfoForGlyph(subcategoryIcon);
    iconChar = getIconForGlyph(subcategoryIcon);
    t2 = getSubcategoryIconStyle(fontInfo.fontName);
    $[1] = subcategoryIcon;
    $[2] = iconChar;
    $[3] = t2;
  } else {
    iconChar = $[2];
    t2 = $[3];
  }
  const iconStyle = t2;
  let t3;
  if ($[4] !== categoryColor || $[5] !== iconStyle) {
    t3 = {
      backgroundColor: categoryColor,
      ...iconStyle
    };
    $[4] = categoryColor;
    $[5] = iconStyle;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== onClick) {
    t4 = () => onClick();
    $[7] = onClick;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t(_temp);
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = hasMedia ? "lg" : "sm";
  const t7 = hasMedia ? mobileMediaCategoryIconStyles : isMobilePane ? mobileCategoryIconStyles : categoryIconStyles;
  let t8;
  if ($[11] !== t7) {
    t8 = css(t7);
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== iconChar || $[14] !== t3 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6 || $[18] !== t8) {
    t9 = <IconButton variant="ghost" style={t3} onClick={t4} color="primary" aria-label={t5} size={t6} className={t8}>{iconChar}</IconButton>;
    $[13] = iconChar;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
};
const headerIconStyles = css.raw({
  boxShadow: '[0px 1px 0px 0px rgba(0,0,0,0.3)]'
});
interface LocationInfoDisplayProps {
  title?: string;
  description?: string;
  imagePath?: string | null;
  imageId?: number | null;
  videoPath?: string | null;
  gameCoords?: MapDataGameCoordinates;
  categoryColor: string;
  subcategoryIcon: string;
  subcategoryTitle?: string;
  onPanToLocation: () => void;
  onClose: () => void;
  isMobilePane?: boolean;
  hideActions?: boolean;
}
const HeaderActions: React.FC<{
  isCustomPin: boolean;
  hasDescription: boolean;
  hasMedia: boolean;
  isMobilePane?: boolean;
  onExpand: () => void;
  onClose: () => void;
  onEmbed: () => void;
}> = t0 => {
  const $ = _c(57);
  const {
    isCustomPin,
    hasDescription,
    hasMedia,
    isMobilePane,
    onExpand,
    onClose,
    onEmbed
  } = t0;
  const buttonTheme = hasMedia ? "white" : "secondary";
  const headerButtonSize = "sm" as const;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["game_map"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useT(t1);
  const {
    handleShare,
    selectedLocation,
    category,
    isCategoryHidden,
    handleToggleCategoryVisibility
  } = useLocationInfo();
  const isCompactViewport = useAppSelector(selectIsCompactViewport);
  const isEditingCustomPin = useAppSelector(selectIsEditingCustomPin);
  const {
    handleEditCustomPin,
    handleDeleteCustomPin,
    isDeletingMarker
  } = useCustomPinOperations();
  const analytics = useAnalytics();
  const hostType = useHostType();
  const currentMapInfo = useAppSelector(selectCurrentMapInfo);
  let t2;
  if ($[1] !== handleEditCustomPin || $[2] !== selectedLocation) {
    t2 = () => {
      if (selectedLocation) {
        handleEditCustomPin(selectedLocation.properties.externalId);
      }
    };
    $[1] = handleEditCustomPin;
    $[2] = selectedLocation;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleEdit = t2;
  let t3;
  if ($[4] !== handleDeleteCustomPin || $[5] !== selectedLocation || $[6] !== t) {
    t3 = async () => {
      if (selectedLocation) {
        ;
        try {
          await handleDeleteCustomPin(selectedLocation.properties.externalId);
        } catch (t4) {
          const error = t4;
          toast({
            title: t(_temp2),
            description: error instanceof Error ? error.message : JSON.stringify(error),
            variant: "error"
          });
        }
      }
    };
    $[4] = handleDeleteCustomPin;
    $[5] = selectedLocation;
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleDelete = t3;
  const showCustomPinActions = isCustomPin && !isEditingCustomPin;
  const t4 = isMobilePane ? "12" : "8";
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = css.raw({
      pointerEvents: "auto"
    });
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== analytics || $[10] !== buttonTheme || $[11] !== category || $[12] !== currentMapInfo?.externalId || $[13] !== handleDelete || $[14] !== handleEdit || $[15] !== handleShare || $[16] !== handleToggleCategoryVisibility || $[17] !== hasMedia || $[18] !== hostType || $[19] !== isCategoryHidden || $[20] !== isCompactViewport || $[21] !== isCustomPin || $[22] !== isDeletingMarker || $[23] !== onEmbed || $[24] !== selectedLocation?.properties.externalId || $[25] !== showCustomPinActions || $[26] !== t) {
    t6 = isCompactViewport ? <DropdownMenu trigger={<IconButton color={buttonTheme} size={headerButtonSize} aria-label={t(_temp3)} className={css(headerIconStyles)}><Icon icon="more_vert" decorative={true} /></IconButton>} placement="bottom-end" onOpenChange={details => {
      if (details.open) {
        analytics.event("game_map_location_info_more_menu_open", {
          mapId: currentMapInfo?.externalId ?? "",
          locationId: selectedLocation?.properties.externalId ?? "",
          platform: hostType
        });
      }
    }}><DropdownMenuItem value="copy-link" leadingIcon="link" onSelect={() => handleShare()}>{t(_temp4)}</DropdownMenuItem>{!isCustomPin && <DropdownMenuItem value="embed" leadingIcon="code" onSelect={onEmbed}>{t(_temp5)}</DropdownMenuItem>}{(category || isCustomPin) && <DropdownMenuItem value="toggle-category" leadingIcon={isCategoryHidden ? "visibility" : "visibility_off"} onSelect={handleToggleCategoryVisibility}>{isCustomPin ? isCategoryHidden ? t(_temp6) : t(_temp7) : isCategoryHidden ? t(_temp8, {
          categoryName: category?.title
        }) : t(_temp9, {
          categoryName: category?.title
        })}</DropdownMenuItem>}{showCustomPinActions && <><DropdownMenuItem value="edit" leadingIcon="edit" onSelect={handleEdit}>{t(_temp10)}</DropdownMenuItem><DropdownMenuItem value="delete" leadingIcon="delete" onSelect={handleDelete} disabled={isDeletingMarker} destructive={true}>{t(_temp11)}</DropdownMenuItem></>}</DropdownMenu> : <>{isCustomPin && <LocationInfoMenu hasMedia={hasMedia} />}<DropdownMenu trigger={<IconButton color={buttonTheme} size={headerButtonSize} aria-label={t(_temp12)} className={css(headerIconStyles)}><Icon icon="ios_share" decorative={true} /></IconButton>} placement="bottom-end">{!isCustomPin && <DropdownMenuItem value="embed" leadingIcon="code" onSelect={onEmbed}>{t(_temp13)}</DropdownMenuItem>}<DropdownMenuItem value="copy-link" leadingIcon="link" onSelect={() => handleShare()}>{t(_temp14)}</DropdownMenuItem></DropdownMenu></>;
    $[9] = analytics;
    $[10] = buttonTheme;
    $[11] = category;
    $[12] = currentMapInfo?.externalId;
    $[13] = handleDelete;
    $[14] = handleEdit;
    $[15] = handleShare;
    $[16] = handleToggleCategoryVisibility;
    $[17] = hasMedia;
    $[18] = hostType;
    $[19] = isCategoryHidden;
    $[20] = isCompactViewport;
    $[21] = isCustomPin;
    $[22] = isDeletingMarker;
    $[23] = onEmbed;
    $[24] = selectedLocation?.properties.externalId;
    $[25] = showCustomPinActions;
    $[26] = t;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== analytics || $[29] !== buttonTheme || $[30] !== currentMapInfo?.externalId || $[31] !== hasDescription || $[32] !== hasMedia || $[33] !== hostType || $[34] !== onExpand || $[35] !== selectedLocation?.properties.externalId || $[36] !== t) {
    t7 = (hasDescription || hasMedia) && <IconButton onClick={() => {
      onExpand();
      analytics.event("game_map_location_info_expand_click", {
        mapId: currentMapInfo?.externalId ?? "",
        locationId: selectedLocation?.properties.externalId ?? "",
        platform: hostType
      });
    }} color={buttonTheme} size={headerButtonSize} aria-label={t(_temp15)} className={css(headerIconStyles)}><Icon icon="open_in_full" decorative={true} /></IconButton>;
    $[28] = analytics;
    $[29] = buttonTheme;
    $[30] = currentMapInfo?.externalId;
    $[31] = hasDescription;
    $[32] = hasMedia;
    $[33] = hostType;
    $[34] = onExpand;
    $[35] = selectedLocation?.properties.externalId;
    $[36] = t;
    $[37] = t7;
  } else {
    t7 = $[37];
  }
  let t8;
  if ($[38] !== analytics || $[39] !== currentMapInfo?.externalId || $[40] !== hostType || $[41] !== onClose || $[42] !== selectedLocation?.properties.externalId) {
    t8 = () => {
      onClose();
      analytics.event("game_map_location_info_close_click", {
        mapId: currentMapInfo?.externalId ?? "",
        locationId: selectedLocation?.properties.externalId ?? "",
        platform: hostType
      });
    };
    $[38] = analytics;
    $[39] = currentMapInfo?.externalId;
    $[40] = hostType;
    $[41] = onClose;
    $[42] = selectedLocation?.properties.externalId;
    $[43] = t8;
  } else {
    t8 = $[43];
  }
  let t9;
  if ($[44] !== t) {
    t9 = t(_temp16);
    $[44] = t;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  let t10;
  let t11;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = css(headerIconStyles);
    t11 = <Icon icon="close" decorative={true} />;
    $[46] = t10;
    $[47] = t11;
  } else {
    t10 = $[46];
    t11 = $[47];
  }
  let t12;
  if ($[48] !== buttonTheme || $[49] !== t8 || $[50] !== t9) {
    t12 = <IconButton onClick={t8} color={buttonTheme} size={headerButtonSize} aria-label={t9} className={t10}>{t11}</IconButton>;
    $[48] = buttonTheme;
    $[49] = t8;
    $[50] = t9;
    $[51] = t12;
  } else {
    t12 = $[51];
  }
  let t13;
  if ($[52] !== t12 || $[53] !== t4 || $[54] !== t6 || $[55] !== t7) {
    t13 = <FlexRow align="center" gap={t4} className={t5}>{t6}{t7}{t12}</FlexRow>;
    $[52] = t12;
    $[53] = t4;
    $[54] = t6;
    $[55] = t7;
    $[56] = t13;
  } else {
    t13 = $[56];
  }
  return t13;
};
export const LocationInfoDisplay: React.FC<LocationInfoDisplayProps> = t0 => {
  const $ = _c(76);
  const {
    title,
    description,
    imagePath,
    imageId,
    videoPath,
    gameCoords,
    categoryColor,
    subcategoryIcon,
    subcategoryTitle,
    onPanToLocation,
    onClose,
    isMobilePane,
    hideActions
  } = t0;
  const [modalOpen, setModalOpen] = React.useState(false);
  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const {
    selectedLocation
  } = useLocationInfo();
  const analytics = useAnalytics();
  const platform = useHostType();
  const hasMedia = !!(imageId || imagePath || videoPath);
  const isCustomPin = selectedLocation?.properties.isCustomPin || false;
  const isCompactViewport = useAppSelector(selectIsCompactViewport);
  const exploreActionMenuPinId = useAppSelector(selectExploreActionMenuPinId);
  const isExploreActionMenu = !isMobilePane && exploreActionMenuPinId === selectedLocation?.properties.externalId && !!selectedLocation;
  const padding = isMobilePane ? CONTENT_PADDING_MOBILE : isCompactViewport ? CONTENT_PADDING_COMPACT : CONTENT_PADDING;
  let t1;
  if ($[0] !== analytics || $[1] !== platform || $[2] !== selectedLocation || $[3] !== subcategoryTitle || $[4] !== title) {
    t1 = (linkUrl, linkText) => {
      if (!selectedLocation) {
        return;
      }
      analytics.event("game_map_description_link_click", {
        locationId: selectedLocation.properties.externalId,
        locationName: title || subcategoryTitle || selectedLocation.properties.externalId,
        linkText,
        linkUrl,
        linkType: getMapDescriptionLinkType(linkUrl),
        platform
      });
    };
    $[0] = analytics;
    $[1] = platform;
    $[2] = selectedLocation;
    $[3] = subcategoryTitle;
    $[4] = title;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleDescriptionLinkClick = t1;
  const t2 = !!description;
  let t3;
  if ($[6] !== description || $[7] !== hasMedia) {
    t3 = () => {
      if (hasMedia && !description) {
        setLightboxOpen(true);
      } else {
        setModalOpen(true);
      }
    };
    $[6] = description;
    $[7] = hasMedia;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setIsEmbedModalOpen(true);
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== hasMedia || $[11] !== isCustomPin || $[12] !== isMobilePane || $[13] !== onClose || $[14] !== t2 || $[15] !== t3) {
    t5 = <HeaderActions isCustomPin={isCustomPin} hasDescription={t2} hasMedia={hasMedia} isMobilePane={isMobilePane} onExpand={t3} onClose={onClose} onEmbed={t4} />;
    $[10] = hasMedia;
    $[11] = isCustomPin;
    $[12] = isMobilePane;
    $[13] = onClose;
    $[14] = t2;
    $[15] = t3;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const headerActions = t5;
  let t6;
  if ($[17] !== categoryColor || $[18] !== hasMedia || $[19] !== headerActions || $[20] !== imageId || $[21] !== imagePath || $[22] !== isCompactViewport || $[23] !== isMobilePane || $[24] !== lightboxOpen || $[25] !== onPanToLocation || $[26] !== padding || $[27] !== subcategoryIcon || $[28] !== subcategoryTitle || $[29] !== title || $[30] !== videoPath) {
    t6 = hasMedia ? <>{!isMobilePane && <div className={css({
        position: "sticky",
        top: "none",
        zIndex: "[4]",
        height: "[0]",
        pointerEvents: "none"
      })}><div className={css({
          position: "absolute",
          top: "none",
          right: "none",
          padding,
          pointerEvents: "auto"
        })}>{headerActions}</div></div>}<div className={css({
        position: "relative"
      })}><PinMedia imageId={imageId} imagePath={imagePath} videoPath={videoPath} title={title || subcategoryTitle} hero={true} compact={isCompactViewport && !isMobilePane} lightboxOpen={lightboxOpen} onLightboxChange={setLightboxOpen} />{isMobilePane && <div className={css({
          position: "absolute",
          top: "none",
          right: "none",
          padding,
          zIndex: "[3]"
        })}>{headerActions}</div>}</div><FlexColumn gap="8" className={css.raw({
        paddingX: padding,
        paddingBottom: "8",
        position: "relative",
        zIndex: "[2]",
        width: "fit",
        pointerEvents: "none"
      })} style={{
        marginTop: "-24px"
      }}><div className={css({
          pointerEvents: "auto"
        })}><CategoryIconButton categoryColor={categoryColor} subcategoryIcon={subcategoryIcon} isMobilePane={isMobilePane} hasMedia={true} onClick={onPanToLocation} /></div><Type variant="header-m" color="white" width="full" title={title || subcategoryTitle} className={css({
          userSelect: "text",
          pointerEvents: "auto"
        })}>{title || subcategoryTitle}</Type></FlexColumn></> : <><FlexRow align="center" justify="between" width="full" className={css.raw({
        padding,
        paddingBottom: "8"
      })}><CategoryIconButton categoryColor={categoryColor} subcategoryIcon={subcategoryIcon} isMobilePane={isMobilePane} onClick={onPanToLocation} />{headerActions}</FlexRow><Type variant="header-m" color="white" width="full" title={title || subcategoryTitle} className={css({
        userSelect: "text",
        paddingX: padding,
        paddingBottom: "8"
      })}>{title || subcategoryTitle}</Type></>;
    $[17] = categoryColor;
    $[18] = hasMedia;
    $[19] = headerActions;
    $[20] = imageId;
    $[21] = imagePath;
    $[22] = isCompactViewport;
    $[23] = isMobilePane;
    $[24] = lightboxOpen;
    $[25] = onPanToLocation;
    $[26] = padding;
    $[27] = subcategoryIcon;
    $[28] = subcategoryTitle;
    $[29] = title;
    $[30] = videoPath;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  let t7;
  if ($[32] !== description || $[33] !== handleDescriptionLinkClick || $[34] !== isMobilePane || $[35] !== padding) {
    t7 = description && (isMobilePane ? <ScrollableContent className={css({
      overflow: "hidden",
      display: "flex",
      flex: "1",
      minHeight: "[0]",
      touchAction: "[pan-y]"
    })} fadeRadius="12px"><FlexColumn width="full" className={css.raw({
        paddingX: padding,
        userSelect: "text"
      })}><LocationContent description={description} onLinkClick={handleDescriptionLinkClick} /></FlexColumn></ScrollableContent> : <FlexColumn width="full" className={css.raw({
      paddingX: padding,
      userSelect: "text"
    })}><LocationContent description={description} onLinkClick={handleDescriptionLinkClick} /></FlexColumn>);
    $[32] = description;
    $[33] = handleDescriptionLinkClick;
    $[34] = isMobilePane;
    $[35] = padding;
    $[36] = t7;
  } else {
    t7 = $[36];
  }
  let t8;
  if ($[37] !== t6 || $[38] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[37] = t6;
    $[38] = t7;
    $[39] = t8;
  } else {
    t8 = $[39];
  }
  const contentSection = t8;
  let t9;
  if ($[40] !== contentSection || $[41] !== isExploreActionMenu || $[42] !== isMobilePane) {
    t9 = isMobilePane ? contentSection : isExploreActionMenu ? null : <ScrollableContent className={css({
      flex: "1",
      minHeight: "[0]",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column"
    })} fadeRadius="16px" scrollAreaStyle={{
      flex: "1",
      minHeight: "0",
      scrollbarWidth: "none"
    }}>{contentSection}</ScrollableContent>;
    $[40] = contentSection;
    $[41] = isExploreActionMenu;
    $[42] = isMobilePane;
    $[43] = t9;
  } else {
    t9 = $[43];
  }
  let t10;
  if ($[44] !== gameCoords || $[45] !== hideActions || $[46] !== isExploreActionMenu || $[47] !== isMobilePane || $[48] !== padding) {
    t10 = !hideActions && <div className={css({
      paddingX: isExploreActionMenu ? "none" : padding,
      flexShrink: isMobilePane ? "0" : undefined
    })}><LocationActions gameCoords={gameCoords} isMobilePane={isMobilePane} /></div>;
    $[44] = gameCoords;
    $[45] = hideActions;
    $[46] = isExploreActionMenu;
    $[47] = isMobilePane;
    $[48] = padding;
    $[49] = t10;
  } else {
    t10 = $[49];
  }
  let t11;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = t12 => {
      const {
        open
      } = t12;
      return setIsEmbedModalOpen(open);
    };
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  let t12;
  if ($[51] !== isEmbedModalOpen) {
    t12 = <MapEmbedModal isOpen={isEmbedModalOpen} onOpenChange={t11} />;
    $[51] = isEmbedModalOpen;
    $[52] = t12;
  } else {
    t12 = $[52];
  }
  let t13;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = t14 => {
      const {
        open: open_0
      } = t14;
      return setModalOpen(open_0);
    };
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  const t14 = title || subcategoryTitle;
  let t15;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = css({
      borderRadius: "8"
    });
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  const t16 = title || subcategoryTitle;
  let t17;
  if ($[55] !== imageId || $[56] !== imagePath || $[57] !== t16 || $[58] !== videoPath) {
    t17 = <PinMedia imagePath={imagePath} imageId={imageId} videoPath={videoPath} title={t16} />;
    $[55] = imageId;
    $[56] = imagePath;
    $[57] = t16;
    $[58] = videoPath;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  let t18;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = css.raw({
      padding: "8"
    });
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  let t19;
  if ($[61] !== description || $[62] !== handleDescriptionLinkClick) {
    t19 = <FlexColumn width="full" className={t18}><LocationContent description={description} onLinkClick={handleDescriptionLinkClick} /></FlexColumn>;
    $[61] = description;
    $[62] = handleDescriptionLinkClick;
    $[63] = t19;
  } else {
    t19 = $[63];
  }
  let t20;
  if ($[64] !== t17 || $[65] !== t19) {
    t20 = <ScrollableContent className={t15} hideScrollbarWhenNoOverflow={true}>{t17}{t19}</ScrollableContent>;
    $[64] = t17;
    $[65] = t19;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  let t21;
  if ($[67] !== modalOpen || $[68] !== t14 || $[69] !== t20) {
    t21 = <Modal isOpen={modalOpen} onOpenChange={t13} size="md" title={t14} disableInternalScrolling={true}>{t20}</Modal>;
    $[67] = modalOpen;
    $[68] = t14;
    $[69] = t20;
    $[70] = t21;
  } else {
    t21 = $[70];
  }
  let t22;
  if ($[71] !== t10 || $[72] !== t12 || $[73] !== t21 || $[74] !== t9) {
    t22 = <>{t9}{t10}{t12}{t21}</>;
    $[71] = t10;
    $[72] = t12;
    $[73] = t21;
    $[74] = t9;
    $[75] = t22;
  } else {
    t22 = $[75];
  }
  return t22;
};
function _temp($) {
  return $.show_on_map;
}
function _temp2($) {
  return $.failed_to_delete_location;
}
function _temp3($_0) {
  return $_0.open_menu;
}
function _temp4($_1) {
  return $_1.copy_link;
}
function _temp5($_2) {
  return $_2.embed;
}
function _temp6($_3) {
  return $_3.show_my_pins;
}
function _temp7($_4) {
  return $_4.hide_my_pins;
}
function _temp8($_5) {
  return $_5.show_category;
}
function _temp9($_6) {
  return $_6.hide_category;
}
function _temp10($_7) {
  return $_7.edit_marker;
}
function _temp11($_8) {
  return $_8.remove_marker;
}
function _temp12($_9) {
  return $_9.share;
}
function _temp13($_10) {
  return $_10.embed;
}
function _temp14($_11) {
  return $_11.copy_link;
}
function _temp15($_12) {
  return $_12.location_info.full_screen_aria_label;
}
function _temp16($_13) {
  return $_13.location_info.close_aria_label;
}